import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, X, Globe, BookOpen, Award } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Specialist = {
  name: string;
  role: string;
  image: string;
  specialty: string;
  profile: string;
  popup?: {
    title: string;
    quote: string;
    experience: string;
    areas: string;
    achievements: string[];
    training: string[];
    memberships: string[];
    languages: string;
  };
};
const specialists: Specialist[] = [{
  name: "MD Bronius Spakauskas, PhD",
  role: "SPINE NEUROSURGEON",
  image: "https://painreliefabroad.ie/wp-content/uploads/2026/05/DSC_4226_2-1-1-scaled.jpg?v=k5m0m6efbw",
  specialty: "Over 40 years of experience in neurosurgery and author of more than 100 peer-reviewed papers.",
  profile: "/our-specialists/bronius-spakauskas",
  popup: {
    title: "MD, PhD, Neurosurgeon",
    quote: "The most important thing is mutual understanding between the doctor and the patient, and the result.",
    experience: "40+ years of experience. Over 10,000 spinal operations performed. Over 100,000 consultations. Highest qualification category since 1993.",
    areas: "Spinal surgery, minimally invasive spine procedures, disc herniation surgery, spinal cord stimulation.",
    achievements: ["Pioneer of minimally invasive spine surgery in Lithuania", "First disc herniation surgery using radiofrequency needle in Lithuania (2009)", "First spinal cord stimulator implantation in the Baltic states (2013)", "First vertebroplasty procedures for spinal disease in Lithuania", "Former Head of Spinal Neurosurgery Dept, LSMU Kauno Klinikos (20+ years)", "Over 100 published scientific papers", "Patent: cervical spine fixation device (LT 5081 B, 2003)"],
    training: ["International neurosurgery conference presentations across Europe", "PubMed publications available"],
    memberships: ["Lithuanian Society of Neurosurgeons"],
    languages: "English (High), Russian"
  }
}, {
  name: "MD Deividas Stukas",
  role: "SPINE NEUROSURGEON",
  image: "https://painreliefabroad.ie/wp-content/uploads/2026/06/DSC_3961_3-3-1-scaled.jpg?v=ofnnxg6d3c",
  specialty: "14 years of experience. Approximately 250 spinal operations per year.",
  profile: "/our-specialists/deividas-stukas",
  popup: {
    title: "MD, Neurosurgeon",
    quote: "My main goal is to help patients live a quality and active life.",
    experience: "14 years of experience including residency. Approximately 250 operations per year. At Clinic Agatas since 2018.",
    areas: "Spinal surgery, consultations.",
    achievements: ["Former Head of Neurosurgery Department, Republican Hospital of Siauliai (2020-2022)", "At LSMU Kauno Klinikos (2017-2020)", "Presentation: Brain tumour removal under nuclear magnetic resonance guidance (2013)"],
    training: ["Salzburg, Austria (2013)", "Antwerp, Belgium (2015)", "Bialystok, Poland (2015)", "Tubingen and Hamburg, Germany (2017-2018)", "Endoscopic Skull Base Surgery, Kaunas (2013)"],
    memberships: ["AO Spine", "Eurospine", "Lithuanian Society of Neurosurgeons"],
    languages: "English (High), Russian"
  }
}, {
  name: "MD Rimvydas Balandziunas",
  role: "SPINE NEUROSURGEON",
  image: "https://painreliefabroad.ie/wp-content/uploads/2026/05/DSC_4251_2-1-1-scaled.jpg?v=h99b6l6dpud",
  specialty: "8 years of experience. Approximately 200 spinal operations per year.",
  profile: "/our-specialists/rimvydas-balandziunas",
  popup: {
    title: "MD, Neurosurgeon",
    quote: "I became a spine surgeon because helping people regain function, reduce pain, and return to the activities they value is deeply meaningful to me. What matters most when treating a patient is listening carefully, understanding their goals, and providing the safest, most effective care tailored to their individual needs.",
    experience: "8 years of experience since residency. Approximately 200 operations per year. Head of Neurosurgery Department, Republican Hospital of Siauliai.",
    areas: "Minimally invasive spine surgery, disc arthroplasty, spinal surgery.",
    achievements: ["Head of Neurosurgery Department, Republican Hospital of Siauliai (from 2022)", "Approximately 200 operations per year", "Board member of Lithuanian Society of Neurosurgeons"],
    training: ["Salzburg, Austria (2021)", "Kassel, Germany (2023)", "Bordeaux, France (2024)"],
    memberships: ["Eurospine", "AO Spine", "Lithuanian Society of Neurosurgeons"],
    languages: "English (Upper Intermediate)"
  }
}];
function SpecialistModal({
  specialist,
  onClose
}: {
  specialist: Specialist;
  onClose: () => void;
}) {
  const p = specialist.popup;
  if (!p) return null;
  return <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 md:p-8" onClick={onClose}>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full md:max-w-[50vw] max-h-[90vh] overflow-y-auto border-4 border-[#264256]" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-[#eefaf4] flex items-center justify-center hover:bg-[#a9e5c9] transition-colors">
          <X className="w-4 h-4 text-[#264256]" />
        </button>
        <div className="flex flex-col md:flex-row">
          <div className="w-full md:w-[240px] flex-shrink-0 md:sticky md:top-0 md:h-fit">
            <img src={specialist.image} alt={specialist.name} className="w-full h-full object-cover object-center rounded-tl-2xl rounded-bl-2xl" style={{
            minHeight: "280px",
            maxHeight: "480px"
          }} />
          </div>
          <div className="flex-1 p-6 flex flex-col gap-4 overflow-y-auto">
            <div>
              <h2 className="font-default font-bold text-[#264256] text-xl leading-tight">{specialist.name}</h2>
              <p className="font-default text-[#65d19d] text-sm font-semibold mt-1">{p.title}</p>
            </div>
            <blockquote className="border-l-4 border-[#65d19d] pl-4">
              <p className="font-default text-[#264256]/80 text-sm leading-relaxed italic">"{p.quote}"</p>
            </blockquote>
            <div className="flex flex-col gap-1">
              <span className="font-default font-bold text-[#264256] text-xs uppercase tracking-wider">Experience</span>
              <p className="font-default text-[#264256] text-sm leading-relaxed">{p.experience}</p>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-default font-bold text-[#264256] text-xs uppercase tracking-wider">Areas of Practice</span>
              <p className="font-default text-[#264256] text-sm leading-relaxed">{p.areas}</p>
            </div>
            <div className="flex flex-col gap-2">
              <span className="font-default font-bold text-[#264256] text-xs uppercase tracking-wider flex items-center gap-1">
                <Award className="w-3.5 h-3.5" /> Key Achievements
              </span>
              <ul className="flex flex-col gap-1">
                {p.achievements.map((item, i) => <li key={i} className="flex items-start gap-2">
                    <span className="text-[#65d19d] shrink-0 mt-0.5">+</span>
                    <span className="font-default text-[#264256] text-sm">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex flex-col gap-2">
              <span className="font-default font-bold text-[#264256] text-xs uppercase tracking-wider flex items-center gap-1">
                <Globe className="w-3.5 h-3.5" /> International Training
              </span>
              <ul className="flex flex-col gap-1">
                {p.training.map((item, i) => <li key={i} className="flex items-start gap-2">
                    <span className="text-[#65d19d] shrink-0 mt-0.5">+</span>
                    <span className="font-default text-[#264256] text-sm">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex flex-col gap-2">
              <span className="font-default font-bold text-[#264256] text-xs uppercase tracking-wider flex items-center gap-1">
                <BookOpen className="w-3.5 h-3.5" /> Professional Memberships
              </span>
              <ul className="flex flex-col gap-1">
                {p.memberships.map((item, i) => <li key={i} className="flex items-start gap-2">
                    <span className="text-[#65d19d] shrink-0 mt-0.5">+</span>
                    <span className="font-default text-[#264256] text-sm">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex flex-col gap-1 pt-2 border-t border-[#f1f3f5]">
              <span className="font-default font-bold text-[#264256] text-xs uppercase tracking-wider">Languages</span>
              <span className="font-default text-[#264256] text-sm">{p.languages}</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default function Neurosurgeons() {
  const {
    ref
  } = useRevealOnScroll();
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeModal, setActiveModal] = useState<Specialist | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [cardW, setCardW] = useState(400);
  useEffect(() => {
    const update = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setCardW(mobile ? containerWidth : 400);
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  const gap = isMobile ? 16 : 32;
  const visibleCount = isMobile ? 1 : 2;
  const maxIndex = Math.max(0, specialists.length - visibleCount);
  return <section id="neurosurgeons" ref={ref} className="bg-background relative w-full py-12 md:py-20 overflow-hidden">
      {activeModal && <SpecialistModal specialist={activeModal} onClose={() => setActiveModal(null)} />}
      <div className="max-w-7xl mx-auto px-4 md:px-12">
        <div className="flex items-center justify-between mb-8 md:mb-10">
          <h3 className="font-default text-2xl md:text-3xl font-bold text-primary">Neurosurgeons</h3>
          <div className="flex gap-3">
            <button onClick={() => setCurrentIndex(i => Math.max(0, i - 1))} disabled={currentIndex === 0} className="w-10 h-10 md:w-12 md:h-12 rounded-md bg-[#a9e5c9] flex items-center justify-center disabled:opacity-30 hover:bg-[#65d19d] transition-colors">
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-[#264256]" />
            </button>
            <button onClick={() => setCurrentIndex(i => Math.min(maxIndex, i + 1))} disabled={currentIndex >= maxIndex} className="w-10 h-10 md:w-12 md:h-12 rounded-md bg-[#a9e5c9] flex items-center justify-center disabled:opacity-30 hover:bg-[#65d19d] transition-colors">
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-[#264256]" />
            </button>
          </div>
        </div>
        <div ref={containerRef} className="relative w-full overflow-hidden py-4">
          <div className="flex transition-transform duration-500 ease-in-out" style={{
          gap: `${gap}px`,
          transform: `translateX(-${currentIndex * (cardW + gap)}px)`
        }}>
            {specialists.map((person, i) => <div key={person.name} data-index={i} className="group relative flex-shrink-0" style={{
            width: isMobile ? `${cardW}px` : "400px"
          }}>
                <Card className="h-full overflow-hidden bg-card transition-all duration-500 rounded-lg shadow-sm hover:shadow-md hover:-translate-y-1 border-border/40">
                  <div className="relative aspect-[3/4] overflow-hidden w-full">
                    <img src={person.image} alt={person.name} className="h-full w-full object-cover object-top transition-all duration-700 ease-in-out scale-100 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute inset-0 flex flex-col items-center justify-end md:justify-center gap-3 pb-5 md:pb-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-500 z-10">
                      {person.popup ? <button onClick={() => setActiveModal(person)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-background/90 backdrop-blur-md border border-border/50 rounded-full text-sm font-default font-medium text-primary hover:bg-primary hover:text-white transition-colors shadow-lg">
                          View Profile <ArrowRight className="w-4 h-4" />
                        </button> : <a href={person.profile} className="inline-flex items-center gap-2 px-5 py-2.5 bg-background/90 backdrop-blur-md border border-border/50 rounded-full text-sm font-default font-medium text-primary hover:bg-primary hover:text-white transition-colors shadow-lg">
                          View Profile <ArrowRight className="w-4 h-4" />
                        </a>}
                    </div>
                  </div>
                  <CardContent className="pt-4 pb-6 px-4 md:pt-6 md:pb-8 md:px-6 text-center relative z-20 bg-card border-t border-border/20">
                    <h3 className="font-default text-xl md:text-2xl font-semibold text-primary mb-2">{person.name}</h3>
                    <div className="flex flex-col gap-1 items-center">
                      <span className="text-xs md:text-sm font-default font-bold text-accent-foreground uppercase tracking-wider">{person.role}</span>
                      <p className="font-default text-sm text-gray-700 mt-2 md:mt-3 leading-relaxed">{person.specialty}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}